import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';
import type { AdminTranslator } from '../../i18n/index.js';
import { terminalsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import type { AdminTerminal, TerminalWithToken } from '../../api/types.js';
import { Banner } from '../../components/Banner.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Modal } from '../../components/Modal.js';
import { SortableColumnHeader } from '../../components/table/SortableColumnHeader.js';
import { FilterSummary, SelectFilterInput, TextFilterInput } from '../../components/table/FilterInputs.js';
import { useTableSort } from '../../lib/dataTable/useTableSort.js';
import { useColumnFilters } from '../../lib/dataTable/useColumnFilters.js';
import { selectColumn, textContainsColumn } from '../../lib/dataTable/filters.js';
import { formatDateTime, hoursSince } from '../../lib/format.js';
import { ProvisioningPanel } from './ProvisioningPanel.js';

const QUIET_HOURS = 6;

/**
 * Self-update / deployed-base visibility (2026-07-31): a compact one-line
 * summary of whatever the terminal last told the sync engine about itself.
 * Every field on `SyncDevice` is optional (a client can send a partial
 * block, or none at all — see @equinox/shared's sync.ts), so this renders
 * only what is actually present rather than a row of dashes.
 */
function formatDeviceInfo(t: AdminTranslator, device: AdminTerminal['deviceInfo']): string {
  if (!device) return t('terminals.deviceUnknown');
  const parts = [device.model, device.platform && device.osVersion ? `${device.platform} ${device.osVersion}` : device.platform ?? device.osVersion].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length > 0 ? parts.join(' · ') : t('terminals.deviceUnknown');
}

function RegisterModal({
  onClose,
  onRegistered,
}: {
  onClose(): void;
  onRegistered(t: TerminalWithToken): void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      onRegistered(await terminalsApi.register({ name: name.trim() }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={t('terminals.new')} onClose={onClose} busy={saving}>
      {error && <Banner kind="danger">{error}</Banner>}
      <label htmlFor="terminal-name">
        {t('terminals.name')}
        <input
          id="terminal-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
          autoFocus
          required
        />
      </label>
      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!name.trim() || saving} onClick={() => void submit()}>
          {saving ? t('common.loading') : t('terminals.register')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Per-terminal time zone (2026-08-01). The device reports its own zone with
 * every sync (Android derives it from network/location — the practical
 * "from GPS"); this modal sets or clears the admin override. The effective
 * zone reaches the terminal in its next sync response.
 */
function TimeZoneModal({
  terminal,
  onClose,
  onSaved,
}: {
  terminal: AdminTerminal;
  onClose(): void;
  onSaved(t: AdminTerminal): void;
}) {
  const { t } = useI18n();
  const [zone, setZone] = useState(terminal.timeZoneOverride ?? terminal.effectiveTimeZone);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const valid = zones.length === 0 || zones.includes(zone);

  async function save(override: string | null) {
    setSaving(true);
    setError(null);
    try {
      onSaved(await terminalsApi.setTimeZoneOverride(terminal.id, override));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
      setSaving(false);
    }
  }

  return (
    <Modal title={t('terminals.timeZoneTitle')} onClose={onClose} busy={saving}>
      {error && <Banner kind="danger">{error}</Banner>}
      <p className="field-hint">
        {terminal.reportedTimeZone
          ? t('terminals.timeZoneReported', { zone: terminal.reportedTimeZone })
          : t('terminals.timeZoneNotReported')}
      </p>
      <label htmlFor="terminal-timezone">
        {t('terminals.timeZoneLabel')}
        <input
          id="terminal-timezone"
          list="terminal-timezone-options"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          disabled={saving}
          autoFocus
        />
        <span className="field-hint">{t('terminals.timeZoneHint')}</span>
      </label>
      <datalist id="terminal-timezone-options">
        {zones.map((z) => (
          <option key={z} value={z} />
        ))}
      </datalist>
      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          disabled={saving || terminal.timeZoneOverride === null}
          onClick={() => void save(null)}
        >
          {t('terminals.timeZoneUseDevice')}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={saving || !zone.trim() || !valid}
          onClick={() => void save(zone.trim())}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}

function TokenOnceModal({ token, onClose }: { token: string; onClose(): void }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <Modal title={t('terminals.tokenOnceTitle')} onClose={onClose}>
      <Banner kind="warn">{t('terminals.tokenOnceWarning')}</Banner>
      <div className="once-reveal">{token}</div>
      <div className="modal__actions">
        <button
          type="button"
          className="btn btn--secondary"
          onClick={async () => {
            await navigator.clipboard?.writeText(token);
            setCopied(true);
          }}
        >
          {copied ? t('common.copied') : t('common.copy')}
        </button>
        <button type="button" className="btn btn--primary" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </Modal>
  );
}

export function TerminalsPage() {
  const { t } = useI18n();
  const [terminals, setTerminals] = useState<AdminTerminal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [issueTarget, setIssueTarget] = useState<AdminTerminal | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<AdminTerminal | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminTerminal | null>(null);
  const [timeZoneTarget, setTimeZoneTarget] = useState<AdminTerminal | null>(null);
  // Tracks which single action is in flight, so the confirm dialog can block
  // ESC/backdrop-close while it awaits the server (house rule: every async
  // action shows an in-progress state, and a destructive one can't be
  // abandoned mid-flight).
  const [busy, setBusy] = useState(false);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setTerminals(await terminalsApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  function upsertTerminal(updated: AdminTerminal) {
    setTerminals((prev) => (prev.some((tm) => tm.id === updated.id) ? prev.map((tm) => (tm.id === updated.id ? updated : tm)) : prev));
  }

  function flashStatus(message: string) {
    setStatus(message);
    window.setTimeout(() => setStatus((current) => (current === message ? null : current)), 4000);
  }

  const columnFilters = useColumnFilters(terminals, {
    name: textContainsColumn<AdminTerminal>((tm) => tm.name),
    appVersion: textContainsColumn<AdminTerminal>((tm) => tm.appVersion ?? ''),
    active: selectColumn<AdminTerminal>((tm) => String(tm.active)),
  });

  const sort = useTableSort(columnFilters.rows, {
    name: { type: 'string', value: (tm: AdminTerminal) => tm.name },
    appVersion: { type: 'string', value: (tm: AdminTerminal) => tm.appVersion },
    lastSeenAt: { type: 'date', value: (tm: AdminTerminal) => tm.lastSeenAt },
    lastSyncAt: { type: 'date', value: (tm: AdminTerminal) => tm.lastSyncAt },
    active: { type: 'boolean', value: (tm: AdminTerminal) => tm.active },
  });
  const visibleTerminals = sort.rows;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('terminals.title')}</h1>
          <p>{t('terminals.subtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" onClick={() => setShowRegister(true)}>
          {t('terminals.new')}
        </button>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}
      {status && <Banner kind="ok">{status}</Banner>}

      <ProvisioningPanel />

      <FilterSummary activeCount={columnFilters.activeCount} onClear={columnFilters.clear} />

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : terminals.length > 0 && visibleTerminals.length === 0 ? (
        <p>{t('table.noMatches')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortableColumnHeader label={t('terminals.name')} ariaSort={sort.ariaSortFor('name')} onSort={() => sort.toggleSort('name')} />
                <SortableColumnHeader
                  label={t('terminals.appVersion')}
                  align="right"
                  ariaSort={sort.ariaSortFor('appVersion')}
                  onSort={() => sort.toggleSort('appVersion')}
                />
                <th className="numeric">{t('terminals.device')}</th>
                <th>{t('terminals.timeZone')}</th>
                <SortableColumnHeader
                  label={t('terminals.lastSeen')}
                  align="right"
                  ariaSort={sort.ariaSortFor('lastSeenAt')}
                  onSort={() => sort.toggleSort('lastSeenAt')}
                />
                <SortableColumnHeader
                  label={t('terminals.lastSync')}
                  align="right"
                  ariaSort={sort.ariaSortFor('lastSyncAt')}
                  onSort={() => sort.toggleSort('lastSyncAt')}
                />
                <SortableColumnHeader label={t('common.status')} ariaSort={sort.ariaSortFor('active')} onSort={() => sort.toggleSort('active')} />
                <th>{t('common.actions')}</th>
              </tr>
              <tr className="table-filter-row">
                <th>
                  <TextFilterInput
                    id="terminals-filter-name"
                    column={t('terminals.name')}
                    value={columnFilters.values.name as string}
                    onChange={(value) => columnFilters.setValue('name', value)}
                  />
                </th>
                <th>
                  <TextFilterInput
                    id="terminals-filter-version"
                    column={t('terminals.appVersion')}
                    value={columnFilters.values.appVersion as string}
                    onChange={(value) => columnFilters.setValue('appVersion', value)}
                  />
                </th>
                <th />
                <th />
                <th />
                <th />
                <th>
                  <SelectFilterInput
                    id="terminals-filter-active"
                    column={t('common.status')}
                    value={columnFilters.values.active as string}
                    onChange={(value) => columnFilters.setValue('active', value)}
                    options={[
                      { value: 'true', label: t('common.active') },
                      { value: 'false', label: t('common.inactive') },
                    ]}
                  />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleTerminals.map((term) => {
                const quietHours = hoursSince(term.lastSyncAt);
                const isQuiet = term.active && (quietHours === null || quietHours > QUIET_HOURS);
                return (
                  <tr key={term.id}>
                    <td>{term.name}</td>
                    <td className="numeric">{term.appVersion ?? '—'}</td>
                    <td className="numeric">{formatDeviceInfo(t, term.deviceInfo)}</td>
                    <td>
                      {term.effectiveTimeZone}
                      {term.timeZoneOverride !== null && (
                        <span className="badge badge--neutral" style={{ marginLeft: 'var(--space-2)' }}>
                          {t('terminals.timeZoneManual')}
                        </span>
                      )}
                    </td>
                    <td className="numeric">{term.lastSeenAt ? formatDateTime(term.lastSeenAt) : t('terminals.never')}</td>
                    <td className="numeric">{term.lastSyncAt ? formatDateTime(term.lastSyncAt) : t('terminals.never')}</td>
                    <td style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', alignItems: 'flex-start' }}>
                      <span className={`badge badge--${term.active ? 'ok' : 'neutral'}`}>
                        {term.active ? t('common.active') : t('common.inactive')}
                      </span>
                      {isQuiet && (
                        <span className="badge badge--warn" title={t('terminals.quietWarning', { hours: QUIET_HOURS })}>
                          {t('terminals.quiet')}
                        </span>
                      )}
                    </td>
                    <td style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setIssueTarget(term)}>
                        {t('terminals.issueToken')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={!term.active}
                        onClick={() => setRevokeTarget(term)}
                      >
                        {t('terminals.revoke')}
                      </button>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDeactivateTarget(term)}>
                        {term.active ? t('terminals.deactivate') : t('terminals.activate')}
                      </button>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setTimeZoneTarget(term)}>
                        {t('terminals.timeZone')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {timeZoneTarget && (
        <TimeZoneModal
          terminal={timeZoneTarget}
          onClose={() => setTimeZoneTarget(null)}
          onSaved={(saved) => {
            setTerminals((prev) => prev.map((tm) => (tm.id === saved.id ? saved : tm)));
            setTimeZoneTarget(null);
          }}
        />
      )}

      {showRegister && (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onRegistered={({ token, ...terminal }) => {
            setTerminals((prev) => [...prev, terminal]);
            setShowRegister(false);
            // The plaintext token only ever exists in this one response body
            // (see packages/server/src/routes/terminals.ts) — if it isn't
            // shown right now, it can never be recovered, so it always opens
            // the same one-time reveal modal a rotation uses.
            setIssuedToken(token);
          }}
        />
      )}

      {issueTarget && (
        <ConfirmDialog
          title={t('terminals.issueToken')}
          consequence={t('terminals.issueTokenConfirm')}
          busy={busy}
          onCancel={() => setIssueTarget(null)}
          onConfirm={async () => {
            const target = issueTarget;
            setBusy(true);
            setError(null);
            try {
              const { token, ...terminal } = await terminalsApi.rotateToken(target.id);
              upsertTerminal(terminal);
              setIssueTarget(null);
              setIssuedToken(token);
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
              setIssueTarget(null);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {issuedToken && <TokenOnceModal token={issuedToken} onClose={() => setIssuedToken(null)} />}

      {revokeTarget && (
        <ConfirmDialog
          title={t('terminals.revoke')}
          consequence={t('terminals.revokeConfirm')}
          danger
          busy={busy}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={async () => {
            const target = revokeTarget;
            setBusy(true);
            setError(null);
            try {
              const updated = await terminalsApi.revoke(target.id);
              upsertTerminal(updated);
              setRevokeTarget(null);
              flashStatus(t('common.saved'));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
              setRevokeTarget(null);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      {deactivateTarget && (
        <ConfirmDialog
          title={deactivateTarget.active ? t('terminals.deactivate') : t('terminals.activate')}
          consequence={t('terminals.deactivateConfirm')}
          danger={deactivateTarget.active}
          busy={busy}
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={async () => {
            const target = deactivateTarget;
            setBusy(true);
            setError(null);
            try {
              const updated = target.active
                ? await terminalsApi.deactivate(target.id)
                : await terminalsApi.activate(target.id);
              upsertTerminal(updated);
              setDeactivateTarget(null);
              flashStatus(t('common.saved'));
            } catch (err) {
              setError(err instanceof ApiError ? err.message : t('common.error'));
              setDeactivateTarget(null);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
